import { useEffect, useMemo } from 'react';

import { Countries, COUNTRIES_WITH_CUSTOM_CURRENCY_INPUT } from '../constants';
import { useAppDispatch, useAppSelector } from '../redux';
import {
  exchangeRatesSelectors,
  fetchExchangeRates,
} from '../redux/exchangeRates';
import { IUserData } from '../types/user';
import { convertCurrencyTo } from '../utils/convertCurrencyTo';
import { formatCompensation } from '../utils/formatCompensation';
import { formatToNumber } from '../utils/formatToNumber';
import { getCompensationAdornment } from '../utils/getCompensationAdornment';

interface ICompensationDisplay {
  symbol: string;
  value: string;
}

export const useCompensationDisplay = (
  user: IUserData,
): ICompensationDisplay => {
  const dispatch = useAppDispatch();
  const rates = useAppSelector(exchangeRatesSelectors.getExchangeRatesData);

  const customCurrencyInput = useMemo(() => {
    return (
      COUNTRIES_WITH_CUSTOM_CURRENCY_INPUT[user?.country as Countries] || null
    );
  }, [user?.country]);

  useEffect(() => {
    if (customCurrencyInput && !rates) {
      dispatch(fetchExchangeRates());
    }
  }, [customCurrencyInput, rates, dispatch]);

  return useMemo(() => {
    const usdDisplay = { symbol: '$', value: formatCompensation(user) };

    if (!customCurrencyInput || !rates || !user?.compensationExpectations) {
      return usdDisplay;
    }

    if (!rates[customCurrencyInput.currency]) return usdDisplay;

    const convertedAmount = convertCurrencyTo(rates)({
      amount: user.compensationExpectations,
      to: customCurrencyInput.currency,
    });

    return {
      symbol: customCurrencyInput.symbol,
      value:
        formatToNumber(convertedAmount) +
        getCompensationAdornment(user.compensationType),
    };
  }, [customCurrencyInput, rates, user]);
};
